import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import categories from 'EventTracking/consts/categories';
import eventTypes from 'EventTracking/consts/eventTypes';
import eventSubTypes from 'EventTracking/consts/eventSubTypes';
import { useCallback } from 'react';
import fr from 'facet-rest';
import Sentry from 'Integrations/Sentry';
import usePlaidNewLink from 'Hooks/Aggregators/useNewPlaidLink';

export function useKYC({ resource, onKYCFinished, onExit }) {
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const handleOnSuccess = useCallback(async (identifier, metadata) => {
    logStandardizedEvent(categories.knowYourCustomer, eventTypes.completed, eventSubTypes.success);
    onKYCFinished({ externalID: metadata.link_session_id });
  }, [logStandardizedEvent, onKYCFinished]);

  const handleEvent = useCallback(async (eventName, metadata) => {
    // Log the event to our vendor specific kyc event table, wrap in a try catch so we do not blow up on fail. This is a background process
    try {
      const eventCollection = await resource.relationship(fr.relationships.knowYourCustomerEvent.name).link('related').get();
      const newEvent = eventCollection.create();
      newEvent.attributes(metadata);
      newEvent.attr('eventName', eventName);
      await newEvent.submit();
    } catch (e) {
      Sentry.createSentryErrorLog(e);
    }
  }, []);

  const handleOnExit = useCallback(async () => {
    logStandardizedEvent(categories.knowYourCustomer, eventTypes.click, eventSubTypes.exiting);
    if (onExit) onExit();
  }, [logStandardizedEvent]);

  const {
    handleClickInitiate,
    isInitialized,
    initializationError,
    failedToOpen,
  } = usePlaidNewLink(resource, {
    onExit: handleOnExit,
    onEvent: handleEvent,
    onSuccess: handleOnSuccess,
    linkToken: resource?.attr('linkToken'),
  });

  const initiateWithEvent = useCallback(async () => {
    logStandardizedEvent(categories.knowYourCustomer, eventTypes.click, eventSubTypes.initializing);
    await handleClickInitiate();
  }, [logStandardizedEvent, handleClickInitiate]);

  return {
    startKYC: initiateWithEvent,
    isInitialized,
    initializationError,
    failedToOpen,
  };
}
